import React, { useMemo } from "react";
import { getMediaUrl } from "../../../../helpers";
import {
  HoverTrackerAvatar,
  HoverTrackerBadge,
  HoverTrackerCompetition,
  HoverTrackerContainer,
  HoverTrackerHint,
  HoverTrackerInner,
  HoverTrackerName,
  HoverTrackerPlayer,
  HoverTrackerRecipient,
} from "./EventPitchTrackerStyles";

const TOOLTIP_WIDTH = 280;
const TOOLTIP_HEIGHT = 195;
const TOOLTIP_OFFSET_X = 16;
const TOOLTIP_OFFSET_Y = 18;
const TOOLTIP_PADDING = 10;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getInitials = (name = "") => {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
};

const toRgba = (color, alpha) => {
  if (!color) {
    return `rgba(200, 255, 0, ${alpha})`;
  }

  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }

    if (hex.length === 6) {
      const r = Number.parseInt(hex.slice(0, 2), 16);
      const g = Number.parseInt(hex.slice(2, 4), 16);
      const b = Number.parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  if (color.startsWith("rgb(")) {
    const values = color
      .replace("rgb(", "")
      .replace(")", "")
      .split(",")
      .map((item) => item.trim());

    if (values.length === 3) {
      return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${alpha})`;
    }
  }

  return `rgba(200, 255, 0, ${alpha})`;
};

const EventHoverTracker = ({
  event,
  visible,
  cursorX,
  cursorY,
  badgeColor,
}) => {
  const position = useMemo(() => {
    if (typeof window === "undefined") {
      return {
        left: TOOLTIP_PADDING,
        top: TOOLTIP_PADDING,
      };
    }

    const maxLeft = Math.max(
      TOOLTIP_PADDING,
      window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_PADDING
    );
    const maxTop = Math.max(
      TOOLTIP_PADDING,
      window.innerHeight - TOOLTIP_HEIGHT - TOOLTIP_PADDING
    );
    const left = clamp(cursorX + TOOLTIP_OFFSET_X, TOOLTIP_PADDING, maxLeft);
    const top = clamp(cursorY - TOOLTIP_OFFSET_Y, TOOLTIP_PADDING, maxTop);

    return {
      left,
      top,
    };
  }, [cursorX, cursorY]);

  const creatorName = event?.creator?.display_name || "";
  const creatorPhoto = event?.creator?.photo ? getMediaUrl(event.creator.photo) : null;
  const homeTeamName = event?.match?.home_team?.name || "";
  const awayTeamName = event?.match?.away_team?.name || "";
  const competitionShortName = event?.match?.competition?.short_name || "";
  const teamsText = [homeTeamName, awayTeamName].filter(Boolean).join(" - ");
  const competitionText = competitionShortName
    ? `${teamsText}${teamsText ? ", " : ""}${competitionShortName}`
    : teamsText;
  const effectiveBadgeColor = badgeColor || "#c8ff00";
  const hasRenderableInfo = Boolean(
    creatorName || creatorPhoto || competitionText || event?.action || event?.recipient?.display_name
  );

  const badgeStyle = useMemo(
    () => ({
      color: effectiveBadgeColor,
      border: `1px solid ${toRgba(effectiveBadgeColor, 0.35)}`,
      background: toRgba(effectiveBadgeColor, 0.16),
    }),
    [effectiveBadgeColor]
  );

  if (!visible || !event || !hasRenderableInfo) {
    return null;
  }

  return (
    <HoverTrackerContainer
      $visible={true}
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      <HoverTrackerInner>
        <HoverTrackerPlayer>
          <HoverTrackerAvatar>
            {creatorPhoto ? <img src={creatorPhoto} alt={creatorName} /> : getInitials(creatorName)}
          </HoverTrackerAvatar>
          <div>
            <HoverTrackerName>{creatorName}</HoverTrackerName>
            <HoverTrackerCompetition>{competitionText}</HoverTrackerCompetition>
          </div>
        </HoverTrackerPlayer>
        <HoverTrackerBadge style={badgeStyle}>{event?.action || ""}</HoverTrackerBadge>
        {/*{event?.recipient?.display_name && (*/}
        {/*  <HoverTrackerRecipient>*/}
        {/*    To <span>{event.recipient.display_name}</span>*/}
        {/*  </HoverTrackerRecipient>*/}
        {/*)}*/}

        {event?.recipient?.display_name && (
            <HoverTrackerRecipient>
              {event.action_id === 65 ? "Goalkeeper" : "To"} <span>{event.recipient.display_name}</span>
            </HoverTrackerRecipient>
        )}

        <HoverTrackerHint>Click to watch clip</HoverTrackerHint>
      </HoverTrackerInner>
    </HoverTrackerContainer>
  );
};

export default EventHoverTracker;
